import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  styled,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import { useMutation as useRestMutation } from 'react-query';
import {
  getPatientQuery,
  GetPatientResponse,
  getQuestionnaireQuery,
  GetQuestionnaireResponse,
  PatientData,
} from './queries';
import { insertQuestionnaireResponseMutation, sendEmail } from './mutations';
import QuestionRenderer from './QuestionRenderer';
import {
  Question,
  UserResponse,
} from '../../../../components/Global/DansFundQuestionnaire/Common';

// Define styled components using the styled API
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontSize: '1.5rem',
  [theme.breakpoints.up('md')]: {
    fontSize: '2rem',
  },
}));


const StyledDivider = styled(Divider)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

// Custom backdrop component since we don't have access to the original
const LoadingBackdrop = styled('div')(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: theme.zIndex.drawer + 1,
}));

interface PatientResponseState {
  answers: UserResponse[];
}

// Define interface for formatted answers to be used in this component and for sending
interface FormattedAnswer {
  questionId: string;
  questionText: string;
  answer: string;
  followUpAnswers?: FormattedAnswer[];
}

const DansFundQuestionnaireComplete = (): React.ReactElement => {
  const { token } = useParams<{ token?: string }>();

  const [patientResponse, setPatientResponse] = useState<PatientResponseState>({
    answers: [],
  });
  const [completed, setCompleted] = useState(false);
  const [allQuestionsAnswered, setAllQuestionsAnswered] = useState(false);
  const [answeredQuestions, setAnsweredQuestions] = useState<string[]>([]);
  // eslint-disable-next-line
  const [_, setForceUpdate] = useState(false);

  const scrolledContainer = useRef<HTMLDivElement>(null);

  const {
    data: questionnaireData,
    loading: questionnaireLoading,
  } = useQuery<GetQuestionnaireResponse>(getQuestionnaireQuery);

  // Query relies on X-Hasura-Patient-Id session variable for row-level security
  // No patientId variable needed - Hasura filters automatically based on the verified token
  const {
    data: patientDataResponse,
    loading: patientLoading,
  } = useQuery<GetPatientResponse>(getPatientQuery, {
    skip: !token,
  });

  // Helper function to recursively format follow-up answers
  const formatFollowUpAnswers = (
    parentAnswerObject: UserResponse, // Renamed from parentAnswer to avoid conflict with outer scope var
    allQuestionDefinitions: Question[], // All questions from the questionnaire structure
    parentQuestionDefinition?: Question, // The question definition to which parentAnswerObject is a response
  ): FormattedAnswer[] => {
    if (!parentAnswerObject.nestedResponses || parentAnswerObject.nestedResponses.length === 0) {
      return [];
    }

    // Determine the source of follow-up question definitions for the current parentAnswerObject
    let followUpQuestionDefinitions: Question[] = [];
    if (parentQuestionDefinition) {
      if (parentAnswerObject.answer === 'yes' && parentQuestionDefinition.yesFollowUps) {
        followUpQuestionDefinitions = parentQuestionDefinition.yesFollowUps;
      } else if (parentAnswerObject.answer === 'no' && parentQuestionDefinition.noFollowUps) {
        followUpQuestionDefinitions = parentQuestionDefinition.noFollowUps;
      }
    }

    return parentAnswerObject.nestedResponses.map(nestedResp => {
      // Find the definition for this nested_response among the potential follow-up questions
      const currentFollowUpQDef = followUpQuestionDefinitions.find(qDef => qDef.id === nestedResp.questionId);

      if (!currentFollowUpQDef) {
        // Fallback if the specific follow-up question definition isn't found in the expected list
        // This might indicate an issue with how follow-up questions are structured or passed.
        // For robustness, we can try a global search as a last resort, but ideally, it's in followUpQuestionDefinitions.
        const globalSearchQDef = allQuestionDefinitions.flatMap(q => [q, ...(q.yesFollowUps || []), ...(q.noFollowUps || [])])
          .find(q => q.id === nestedResp.questionId);
        const questionText = globalSearchQDef?.text || `Unknown Follow-up ID: ${nestedResp.questionId}`;
        console.warn(`Follow-up question definition for ID ${nestedResp.questionId} not found in direct parent, used fallback text: ${questionText}`);
        return {
          questionId: nestedResp.questionId,
          questionText: questionText,
          answer: nestedResp.answer,
          // Recursive call: Pass the found globalSearchQDef (if any) as the new parentQuestionDefinition
          followUpAnswers: formatFollowUpAnswers(nestedResp, allQuestionDefinitions, globalSearchQDef),
        };
      }

      return {
        questionId: nestedResp.questionId,
        questionText: currentFollowUpQDef.text,
        answer: nestedResp.answer,
        // Recursive call: Pass currentFollowUpQDef as the parent for its own nested responses
        followUpAnswers: formatFollowUpAnswers(nestedResp, allQuestionDefinitions, currentFollowUpQDef),
      };
    }).filter(Boolean) as FormattedAnswer[];
  };

  const {
    mutateAsync: sendEmailAsync,
    isLoading: sendingEmail,
  } = useRestMutation({
    mutationKey: 'send-email',
    mutationFn: (request: any) => sendEmail(request),
  });

  // Function to send questionnaire results via email
  const sendQuestionnaireEmail = async () => {
    try {
      const patient = patientDataResponse?.dans_fund_patients?.[0];
      if (!patient || !questionnaireData || !patientResponse.answers.length) {
        console.error('Missing data for sending email: patient, questionnaire, or answers.');
        return;
      }
      const questions = questionnaireData.dans_fund_questionnaire[0].questions as Question[];
      const send_to = questionnaireData.dans_fund_questionnaire[0].send_to;
      const formattedAnswers: FormattedAnswer[] = patientResponse.answers.map(answer => {
        const question = questions.find(q => q.id === answer.questionId);
        return {
          questionId: answer.questionId,
          questionText: question?.text || 'Unknown Question',
          answer: answer.answer,
          // Initial call to formatFollowUpAnswers: pass the main question definition
          followUpAnswers: formatFollowUpAnswers(answer, questions, question),
        };
      });
      console.log(send_to);
      const emailData = {
        recipientEmail: send_to,
        subject: `Questionnaire Results - Patient Number: ${patient.phone_number}`,
        patient: {
          patientNumber: patient.phone_number,
        },
        answers: formattedAnswers,
      };

      console.log('Sending email with data:', JSON.stringify(emailData));

      const response = await sendEmailAsync(JSON.stringify(emailData));
      console.log('Email sent successfully:', response.data);
    } catch (error: any) { // Explicitly type error as any
      console.error('Error sending questionnaire results email:', error.response ? error.response.data : error.message);
    }
  };

  const [insertResponse, { loading: insertLoading }] = useMutation(insertQuestionnaireResponseMutation, {
    onCompleted: () => {
      setCompleted(true);
      if (patientDataResponse?.dans_fund_patients?.[0] && questionnaireData) {
        sendQuestionnaireEmail();
      }
    },
    onError: (error) => {
      console.error('Error inserting questionnaire response:', error);
      // Handle error appropriately, e.g., show a message to the user
    },
  });

  const checkIfAllQuestionsAnswered = useCallback(() => {
    if (!questionnaireData || !questionnaireData.dans_fund_questionnaire.length) return;

    const questions = questionnaireData.dans_fund_questionnaire[0].questions as Question[];
    const allAnswered = questions.every(question =>
      patientResponse.answers.some(ans => ans.questionId === question.id),
    );
    setAllQuestionsAnswered(allAnswered);
  }, [patientResponse.answers, questionnaireData]);

  const handleOptionSelect = useCallback((response: UserResponse) => {
    setPatientResponse(prevResponse => {
      const existingAnswerIndex = prevResponse.answers.findIndex((a) => a.questionId === response.questionId);
      const updatedAnswers = [...prevResponse.answers];
      if (existingAnswerIndex >= 0) {
        updatedAnswers[existingAnswerIndex] = response;
      } else {
        updatedAnswers.push(response);
      }
      setAnsweredQuestions(currentAnswered => {
        const newAnswered = new Set(currentAnswered);
        newAnswered.add(response.questionId);
        return Array.from(newAnswered);
      });
      return { ...prevResponse, answers: updatedAnswers };
    });

    setTimeout(() => {
      if (questionnaireData && questionnaireData.dans_fund_questionnaire.length > 0) {
        const questions = questionnaireData.dans_fund_questionnaire[0].questions as Question[];
        const currentQuestion = questions.find((q) => q.id === response.questionId);
        if (!currentQuestion) return;

        const hasFollowUp = response.answer === 'yes'
          ? !!(currentQuestion.yesFollowUps && currentQuestion.yesFollowUps.length > 0)
          : !!(currentQuestion.noFollowUps && currentQuestion.noFollowUps.length > 0);

        if (hasFollowUp) {
          setForceUpdate(prev => !prev);
          if (scrolledContainer.current) {
            scrolledContainer.current.scrollTop = 0;
          }
        }
      }
    }, 0);
  }, [questionnaireData, scrolledContainer]);

  useEffect(() => {
    if (questionnaireData && questionnaireData.dans_fund_questionnaire.length > 0 && patientResponse.answers.length > 0) {
      checkIfAllQuestionsAnswered();
    } else if (allQuestionsAnswered) {
      setAllQuestionsAnswered(false);
    }
  }, [patientResponse.answers, questionnaireData, checkIfAllQuestionsAnswered, allQuestionsAnswered]);

  if (questionnaireLoading || patientLoading || insertLoading) {
    return (
      <LoadingBackdrop>
        <CircularProgress color="primary" />
      </LoadingBackdrop>
    );
  }

  if (!questionnaireData || !questionnaireData.dans_fund_questionnaire.length) {
    return (
      <StyledPaper>
        <Typography variant="h5">No questionnaire found</Typography>
      </StyledPaper>
    );
  }

  if (!token) {
    return (
      <StyledPaper>
        <Typography variant="h5">Invalid Link</Typography>
        <Typography variant="body1">Please use the link from your SMS to access the
          questionnaire.</Typography>
      </StyledPaper>
    );
  }

  if (patientLoading) {
    return (
      <LoadingBackdrop>
        <CircularProgress color="secondary" />
        <Typography sx={{ ml: 2, color: 'white' }}>Loading patient
          details...</Typography>
      </LoadingBackdrop>
    );
  }

  const patient = patientDataResponse?.dans_fund_patients?.[0];
  if (!patient) {
    return (
      <StyledPaper>
        <Typography variant="h5">Link Invalid or Expired</Typography>
        <Typography variant="body1">Could not verify your identity. The link may be
          expired or invalid. Please contact support.</Typography>
      </StyledPaper>
    );
  }

  const handleSubmit = () => {
    if (!questionnaireData || !token || !patient) {
      console.error('handleSubmit called with missing data or invalid token.');
      return;
    }

    // patient_id is auto-set by Hasura from X-Hasura-Patient-Id session variable
    const responseInput = {
      response_data: JSON.stringify(patientResponse.answers),
    };

    insertResponse({
      variables: {
        object: responseInput,
      },
    });
  };


  return (
    <StyledPaper ref={scrolledContainer}>
      <Title variant="h4">
        {questionnaireData?.dans_fund_questionnaire[0]?.name}
      </Title>

      {completed ? (
        <>
          <Alert severity="success" sx={{ mb: 2 }}>
            <AlertTitle>Success</AlertTitle>
            Thank you for completing the questionnaire. The results have been
            submitted.
          </Alert>
          <StyledDivider />
        </>
      ) : (
        <>
          {questionnaireData?.dans_fund_questionnaire[0]?.questions.map((question: Question, index: number) => (
            <Box key={question.id} sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
                Question {index + 1} of {questionnaireData?.dans_fund_questionnaire[0]?.questions.length}
              </Typography>
              <QuestionRenderer
                question={question}
                onOptionSelected={handleOptionSelect}
              />
            </Box>
          ))}

          <Box sx={{
            mt: 3,
            display: 'flex',
            justifyContent: 'space-between',
            position: 'sticky',
            bottom: 0,
            pt: 2,
            pb: 2,
            bgcolor: 'background.paper',
            zIndex: 1,
            boxShadow: '0px -2px 4px rgba(0, 0, 0, 0.05)',
            borderTop: '1px solid rgba(0, 0, 0, 0.08)',
          }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={!allQuestionsAnswered || patientResponse.answers.length === 0 || insertLoading}
              size="large"
              sx={{
                minWidth: '180px',
                fontWeight: 500,
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.26)',
                },
              }}
            >
              {(insertLoading || sendingEmail) ?
                <CircularProgress size={24} /> : 'Submit Questionnaire'}
            </Button>
          </Box>
        </>
      )}
    </StyledPaper>
  );
};

export default DansFundQuestionnaireComplete;
